import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared dialog to add a single referral to a partner (Admin only).
 * Used from both AdminPartners and AdminCRM.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   partner: { user_id, name }  — required; drives the title + endpoint
 *   onAdded: (result) => void   — optional callback after successful add
 */
export const AddReferralDialog = ({ open, onOpenChange, partner, onAdded }) => {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
    }
  }, [open]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  const handleSubmit = async () => {
    if (!partner?.user_id) return;
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!form.customer_email.trim() && !form.customer_phone.trim()) {
      toast.error('Either email or phone is required');
      return;
    }
    setSubmitting(true);
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/admin/referrals/add/${partner.user_id}`, true);
      const headers = getAuthHeaders();
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.onload = () => {
        setSubmitting(false);
        let result;
        try { result = JSON.parse(xhr.responseText); } catch (e) { result = null; }
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(result?.message || 'Referral added');
          onOpenChange(false);
          if (onAdded) onAdded(result);
        } else if (xhr.status === 409) {
          const msg = result?.detail?.message || 'Referral cannot be added';
          toast.error(msg, { duration: 6000 });
        } else if (xhr.status === 422 || xhr.status === 400) {
          const msg = (result && (result.detail?.message || result.detail))
            || 'Please check the fields';
          toast.error(typeof msg === 'string' ? msg : 'Validation error');
        } else {
          toast.error(`Failed to add referral (${xhr.status})`);
        }
      };
      xhr.onerror = () => { setSubmitting(false); toast.error('Network error'); };
      xhr.send(JSON.stringify(form));
    } catch (err) {
      setSubmitting(false);
      toast.error('Failed: ' + err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-referral-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Referral{partner?.name ? ` for ${partner.name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Quick-add a single referral to this partner. The same duplicate-guards as bulk upload apply.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="ref-name">Customer Name *</Label>
            <Input
              id="ref-name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Full name"
              data-testid="add-ref-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ref-email">Email</Label>
              <Input
                id="ref-email"
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                placeholder="customer@example.com"
                data-testid="add-ref-email"
              />
            </div>
            <div>
              <Label htmlFor="ref-phone">Phone</Label>
              <Input
                id="ref-phone"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                placeholder="+91 XXXXXXXXXX"
                data-testid="add-ref-phone"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Provide at least one — email or phone.</p>
          <div>
            <Label htmlFor="ref-notes">Notes</Label>
            <Textarea
              id="ref-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional context (e.g., met at gym, Instagram DM, etc.)"
              rows={2}
              data-testid="add-ref-notes"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="add-ref-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Add Referral
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddReferralDialog;
